import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, OverlayTrigger, Row, Tab, Tabs, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faArrowCircleLeft, faRedoAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import ActionDropDown from 'components/shared/ActionDropDown';
import { areYouSure } from 'lib/utils/toast';
import ConcurrentGDSNSyncs from 'components/data_sources/single_data_source_view/ConcurrentGDSNSyncs';
import Container from 'components/shared/Container';
import {
  DATA_SOURCE_ASSIGN_SKUS,
  DATA_SOURCE_GET_BY_ID,
  DATA_SOURCE_IN_PROGRESS_RUNS,
  DATA_SOURCE_LAST_RUN_DATA,
  DATA_SOURCE_RUN_DATA,
  DATA_SOURCE_PROCESS,
  DATA_SOURCE_COMMENTS_GET,
  PRESIGNED_URL,
  getURL,
} from 'lib/networking/endpoints';
import DATA_SOURCE_CATEGORY from 'lib/enums/DataSourceCategory';
import { DATA_SOURCE_IMPORT_TYPES } from 'lib/enums/DataSourceEntity';
import { DATA_SOURCES_VIEW_TABS } from 'lib/constants';
import DataSourceSummaryTab from 'components/data_sources/single_data_source_view/DataSourceSummaryTab';
import DataSourceProductsTab from 'components/data_sources/single_data_source_view/DataSourceProductsTab';
import DataSourceAttributesTab from 'components/data_sources/single_data_source_view/DataSourceAttributesTab';
import DataSourceSkuMappingsTab from 'components/data_sources/single_data_source_view/DataSourceSkuMappingsTab';
import DataSourceEditTab from 'components/data_sources/single_data_source_view/DataSourceEditTab';
import DataSourceSubscribedManufacturersTab from 'components/data_sources/single_data_source_view/DataSourceSubscribedManufacturersTab';
import GDSNDataSourceManufacturersTab from 'components/data_sources/single_data_source_view/GDSNDataSourceManufacturersTab';
import GDSNSelectManufacturerModal from 'components/data_sources/single_data_source_view/GDSNSelectManufacturerModal';
import DataSourceCommentModal from 'components/data_sources/single_data_source_view/DataSourceCommentModal';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import pointerOnHover from 'lib/css/pointerOnHover';
import SingleDataSourceRunDataTable from 'components/data_sources/single_data_source_view/SingleDataSourceRunDataTable';
import SwalDialog from 'lib/utils/toast';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

function SingleDataSourceView() {
  const { record_id } = useParams();
  const history = useHistory();
  const location = useLocation();

  const {
    data: dataSource = {},
    loading,
    error,
    refetch,
  } = useGet(getURL(DATA_SOURCE_GET_BY_ID, { data_source_id: record_id }));

  const {
    data: dataSourceRun = {},
    loading: loadingRun,
    refetch: refetchRun,
  } = useGet(
    DATA_SOURCE_LAST_RUN_DATA,
    {
      data_source_id: record_id,
    },
    !record_id,
  );

  const { data: dataSourceRuns = [], loading: loadingRunData } = useGet(
    DATA_SOURCE_RUN_DATA,
    {
      data_source_id: record_id,
    },
    !record_id,
  );

  const {
    data: inProgressRuns = [],
    loading: loadingInProgressRuns,
    refetch: refetchInProgressRuns,
  } = useGet(
    DATA_SOURCE_IN_PROGRESS_RUNS,
    { data_source_id: record_id },
    !record_id || dataSource.source_type !== DATA_SOURCE_CATEGORY.GDSN,
  );

  const { data: { data: comments = [] } = {} } = useGet(
    getURL(DATA_SOURCE_COMMENTS_GET, { data_source_id: record_id }),
    {},
    !record_id,
  );

  useEffect(() => {
    if (dataSourceRun && dataSourceRun.run_metadata) {
      dataSourceRun.run_metadata.retries = dataSourceRun.retries || 0;
    }
    // Enhanced polling for GDSN concurrent syncs
    if (dataSource.source_type === DATA_SOURCE_CATEGORY.GDSN && inProgressRuns.length > 0) {
      const interval = setInterval(() => {
        refetchInProgressRuns();
        refetchRun(); // Still update the latest run
      }, 5000); // Poll every 5 seconds for active syncs

      return () => clearInterval(interval);
    } else if (dataSourceRun && dataSourceRun.in_progress) {
      // Original polling for non-GDSN data sources
      const interval = setInterval(() => {
        refetchRun();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [dataSource, inProgressRuns, dataSourceRun, refetchInProgressRuns, refetchRun]);

  const { postData: processDataSource, loading: processing } = usePost(DATA_SOURCE_PROCESS);

  const { postData: getPresignedUrl } = usePost(
    PRESIGNED_URL,
    res => {
      window.location.href = res.data.presigned_url;
    },
    () => {
      SwalDialog('error', 'An error occurred while downloading the file', 'Error', 'center');
    },
  );

  const { postData: assignUPBSKUs, loading: assigningUPBSKUs } = usePost(
    DATA_SOURCE_ASSIGN_SKUS,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Started assigning UPB SKUs',
        title: 'Success',
        timer: 500,
      }).then(() => {
        refetch();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: `An error occurred while assigning UPB SKUs: ${error?.response?.data?.message}`,
        title: 'Error',
      }),
  );

  const [activeTabKey, setActiveTabKey] = useState();
  const [showSelectManufacturerModal, setShowSelectManufacturerModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);

  const gdsnDataSourceWithoutManufacturer = useMemo(() => {
    return (
      dataSource.source_type === DATA_SOURCE_CATEGORY.GDSN &&
      !dataSource.global_manufacturer &&
      !dataSource.global_vendor
    );
  }, [dataSource]);

  useEffect(() => {
    // Only set the default active tab on initial load, not on refetch
    if (dataSource?.import_type && !activeTabKey) {
      const tabFromQuery = new URLSearchParams(location.search).get('tab');
      if (tabFromQuery && Object.values(DATA_SOURCES_VIEW_TABS).includes(tabFromQuery)) {
        setActiveTabKey(tabFromQuery);
      } else if (gdsnDataSourceWithoutManufacturer) {
        setActiveTabKey(DATA_SOURCES_VIEW_TABS.PRODUCTS);
      } else {
        setActiveTabKey(
          dataSource?.import_type === DATA_SOURCE_IMPORT_TYPES.ATTRIBUTE_IMPORT
            ? DATA_SOURCES_VIEW_TABS.ATTRIBUTES
            : dataSource?.import_type === DATA_SOURCE_IMPORT_TYPES.SKU_MAPPING_IMPORT
            ? DATA_SOURCES_VIEW_TABS.SKU_MAPPINGS
            : DATA_SOURCES_VIEW_TABS.PRODUCTS,
        );
      }
    }
  }, [dataSource, gdsnDataSourceWithoutManufacturer, activeTabKey, location.search]);

  const runInProgress = useMemo(
    () => loadingRun || (dataSourceRun && dataSourceRun.in_progress),
    [loadingRun, dataSourceRun],
  );

  const assigningSKUs = dataSource?.upb_sku_status;

  if (loading || loadingRunData) {
    return <LoadingSpinner />;
  }

  if (error) {
    return null;
  }

  if (!dataSource) {
    return <h3 className="my-5 text-center">Data Source not found</h3>;
  }

  function handleProcessNow(deltaFile = false) {
    processDataSource({ data_source_id: record_id, process_delta_file: deltaFile }).then(() => {
      setTimeout(() => {
        refetchRun();
      }, 1000);
    });
  }

  const isGDSN = dataSource.source_type === DATA_SOURCE_CATEGORY.GDSN;
  const hasActiveRuns = inProgressRuns.length > 0;

  // For GDSN: Allow multiple concurrent syncs
  // For others: Block if any run in progress
  const shouldDisableProcessing = isGDSN ? false : runInProgress;

  return (
    <Container>
      <GDSNSelectManufacturerModal
        show={showSelectManufacturerModal}
        onHide={() => setShowSelectManufacturerModal(false)}
        dataSource={dataSource}
        processDataSource={processDataSource}
        refetchRun={refetchRun}
      />
      <Row className="mr-3">
        <Col>
          <h3 className="font-weight-bold">
            <FontAwesomeIcon
              className={classNames(pointerOnHover, 'mr-2')}
              icon={faArrowCircleLeft}
              onClick={() => history.goBack()}
            />
            {dataSource.source_name}
          </h3>
        </Col>
        <Col className="ml-auto col-auto">
          <Row className="d-flex flex-column">
            <Col className="text-right d-flex justify-content-end">
              {dataSource && dataSource.assign_upb_skus && (
                <Button
                  variant="outline-primary"
                  disabled={runInProgress || processing || assigningSKUs || assigningUPBSKUs}
                  onClick={() => assignUPBSKUs({ data_source_id: record_id })}
                >
                  {assigningSKUs ? 'assigning SKUs, please wait' : 'Assign UPB SKUs'}
                </Button>
              )}
              <Button variant="primary" className="ml-2" onClick={() => setShowCommentModal(true)}>
                Comments{' '}
                {comments?.length > 0 && (
                  <span className="badge badge-light ml-1">{comments.length}</span>
                )}
              </Button>
              {dataSource.is_recurring && !isGDSN ? (
                <ActionDropDown
                  className="ml-2"
                  submenuItems={[
                    {
                      title: 'Process the delta file',
                      action: () => handleProcessNow(true),
                    },
                    {
                      title: 'Process the entire file',
                      action: () => handleProcessNow(false),
                    },
                  ]}
                  label={runInProgress ? 'Run in progress, please wait' : 'Process Now'}
                />
              ) : (
                <Button
                  variant="outline-primary"
                  disabled={shouldDisableProcessing || processing}
                  className="ml-2"
                  onClick={() => handleProcessNow(false)}
                >
                  {isGDSN
                    ? `Process Now ${hasActiveRuns ? `(+${inProgressRuns.length} running)` : ''}`
                    : runInProgress
                    ? 'Run in progress, please wait'
                    : 'Process Now'}
                </Button>
              )}
              {gdsnDataSourceWithoutManufacturer && (
                <Button
                  variant="outline-success"
                  disabled={runInProgress || processing || assigningSKUs}
                  className="ml-2"
                  onClick={() => setShowSelectManufacturerModal(true)}
                >
                  Process Manufacturer
                </Button>
              )}
              <OverlayTrigger overlay={<Tooltip>Forcefully resume/restart this process</Tooltip>}>
                <Button
                  disabled={processing || !runInProgress}
                  className="ml-2"
                  variant="danger"
                  onClick={() =>
                    areYouSure(
                      () =>
                        processDataSource({
                          data_source_id: record_id,
                          forcefully_resume: true,
                        }).then(() => {
                          setTimeout(() => {
                            refetchRun();
                          }, 1000);
                        }),
                      'This will forcefully restart/resume the process. ' +
                        "This is not recommended unless you know what you're doing.",
                    )
                  }
                >
                  <FontAwesomeIcon icon={faRedoAlt} />
                </Button>
              </OverlayTrigger>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="mr-3">
        <Col className="text-right mt-1">
          <Row className="text-right">
            <Col>
              {dataSourceRun?.run_metadata && (
                <div className={classNames(css(styles.smallText), 'text-muted')}>
                  {JSON.stringify(dataSourceRun.run_metadata, null, 2)}
                  {loadingRun && (
                    <span className={classNames(css(styles.smallText), 'text-muted ml-2')}>
                      Loading...
                    </span>
                  )}
                </div>
              )}

              {dataSourceRun?.error_report_url && (
                <div className={classNames(css(styles.smallText), 'text-muted')}>
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      getPresignedUrl({
                        file_url: dataSourceRun.error_report_url,
                        force_download: true,
                      });
                    }}
                  >
                    Error Report
                  </a>
                </div>
              )}
            </Col>
          </Row>
        </Col>
      </Row>

      <Tabs
        className="mt-3 border-top-0"
        activeKey={activeTabKey}
        onSelect={selectedKey => setActiveTabKey(selectedKey)}
        transition={false}
        mountOnEnter={true}
      >
        {[DATA_SOURCE_IMPORT_TYPES.ATTRIBUTE_IMPORT, DATA_SOURCE_IMPORT_TYPES.PDF_IMPORT].includes(
          dataSource.import_type,
        ) ? (
          <Tab
            eventKey={DATA_SOURCES_VIEW_TABS.ATTRIBUTES}
            title={DATA_SOURCES_VIEW_TABS.ATTRIBUTES}
          >
            <DataSourceAttributesTab dataSource={dataSource} refetch={refetch} />
          </Tab>
        ) : dataSource.import_type === DATA_SOURCE_IMPORT_TYPES.SKU_MAPPING_IMPORT ? (
          <Tab
            eventKey={DATA_SOURCES_VIEW_TABS.SKU_MAPPINGS}
            title={DATA_SOURCES_VIEW_TABS.SKU_MAPPINGS}
          >
            <DataSourceSkuMappingsTab dataSource={dataSource} refetch={refetch} />
          </Tab>
        ) : null}
        <Tab eventKey={DATA_SOURCES_VIEW_TABS.PRODUCTS} title={DATA_SOURCES_VIEW_TABS.PRODUCTS}>
          {gdsnDataSourceWithoutManufacturer ? (
            <GDSNDataSourceManufacturersTab
              dataSource={dataSource}
              processDataSource={processDataSource}
              processing={processing}
              refetchRun={refetchRun}
            />
          ) : (
            <DataSourceProductsTab dataSource={dataSource} />
          )}
        </Tab>
        {gdsnDataSourceWithoutManufacturer && (
          <Tab
            eventKey={DATA_SOURCES_VIEW_TABS.SUBSCRIBED_MANUFACTURERS}
            title={DATA_SOURCES_VIEW_TABS.SUBSCRIBED_MANUFACTURERS}
          >
            <DataSourceSubscribedManufacturersTab
              dataSource={dataSource}
              processDataSource={processDataSource}
              processing={processing}
              refetchRun={refetchRun}
            />
          </Tab>
        )}
        <Tab eventKey={DATA_SOURCES_VIEW_TABS.SUMMARY} title={DATA_SOURCES_VIEW_TABS.SUMMARY}>
          <DataSourceSummaryTab dataSource={dataSource} dataSourceRun={dataSourceRun} />
        </Tab>
        <Tab eventKey={DATA_SOURCES_VIEW_TABS.SETTINGS} title={DATA_SOURCES_VIEW_TABS.SETTINGS}>
          <DataSourceEditTab dataSource={dataSource} refetch={refetch} />
        </Tab>
        {/* Concurrent Syncs Tab - Only show for GDSN with active runs */}
        {isGDSN && hasActiveRuns && (
          <Tab
            eventKey="CONCURRENT_SYNCS"
            title={
              <span>
                🔄 Active Syncs{' '}
                <span className="badge badge-primary ml-1">{inProgressRuns.length}</span>
              </span>
            }
          >
            <div className="mt-4">
              <ConcurrentGDSNSyncs
                inProgressRuns={inProgressRuns}
                dataSource={dataSource}
                loading={loadingInProgressRuns}
              />
            </div>
          </Tab>
        )}
        <Tab
          eventKey={DATA_SOURCES_VIEW_TABS.PROCESS_LOGS}
          title={DATA_SOURCES_VIEW_TABS.PROCESS_LOGS}
        >
          <SingleDataSourceRunDataTable
            dataSourceRuns={dataSourceRuns}
            inProgressRuns={inProgressRuns}
            dataSource={dataSource}
          />
        </Tab>
      </Tabs>

      {/* Comment Modal */}
      <DataSourceCommentModal
        show={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        dataSourceId={parseInt(record_id)}
        dataSourceName={dataSource.source_name}
      />
    </Container>
  );
}

const styles = StyleSheet.create({
  smallText: {
    fontSize: '0.8rem',
  },
});

DataSourceSummaryTab.propTypes = {
  dataSource: PropTypes.object,
};

export default SingleDataSourceView;
